/* eslint-disable  no-nested-ternary */
/* eslint-disable  max-len */
/* eslint-disable  consistent-return */

import { FC, useRef, useState, JSX, useEffect } from 'react';
import FileUploadModal from 'components/FileUpload/FileUploadModal';
import { Button, Space, Tag, Comment, Typography, Tooltip, Popover } from 'antd';
import { CloseOutlined, UploadOutlined, EditOutlined } from '@ant-design/icons';
import RichTextEditor, { RichTextEditorHandle } from 'components/RichTextEditor';
import { FaQuoteLeft, FaRegSmile } from 'react-icons/fa';
import { MdOutlineTextFormat } from 'react-icons/md';
import UserAvatarPopover from 'components/UserAvatarPopover';
import { convertSnakeCaseToTitleCase, deltaToHtml, mentionCommentParser, parseDateToUSFormat } from 'utils/string.utils';
import { UserResponse } from 'types';
import FileViewerModal from 'components/FileViewerModal';
import { EmojiPicker } from 'components/EmojiPicker';
import { PickerPositioner } from 'components/EmojiPicker/PickerPositioner';
import { UploadFileApi } from 'types/upload-type';
import CommentActions, { CommentActionsParentProps, CommentActionsChildrenProps } from './CommentActions';
import CommentUploads from './CommentUploads';
import { CommentSchedule } from './CommentSchedule';
import styles from './styles.module.less';

const { Text } = Typography;

interface CommentCardBaseProps {
  comment: any;
  user: UserResponse;
  projectUuid: string;
  typeUuid: string;
  targetCommentId: any;
  type: 'tasks' | 'projects' | 'requests';
  scrollingContainer?: string | HTMLElement | undefined;
  mentionTypes?: ('regular_user' | 'freelancer' | 'contact' | 'group')[];
  isProjectPersonal?: boolean;
  mentionableUrl?: string;
  prefetchedMentionables?: any[];
  canComment?: boolean;
  searchTerm?: string;
  onImgDelete: any;
  onReplyTo: CommentActionsParentProps['onReplyTo'];
  onQuoteTo: (uuid: string, comment: any) => void;
  onScrollToComment?: (uuid: string) => void;
  onImageModalChanged?: (open: boolean) => void;
  onAddReplyReaction?: CommentActionsChildrenProps['onAddReaction'];
  onRemoveReplyReaction?: CommentActionsChildrenProps['onRemoveReaction'];
  commentReplies?: any;
}

interface CommentCardParentProps extends CommentCardBaseProps {
  parentEl?: undefined;
  onAddReaction: CommentActionsParentProps['onAddReaction'];
  onRemoveReaction: CommentActionsParentProps['onRemoveReaction'];
  onUpdate: CommentActionsParentProps['onUpdate'];
  onUpdateSchedule?: CommentActionsParentProps['onUpdateSchedule'];
  onDelete: CommentActionsParentProps['onDelete'];
}

interface CommentCardChildrenProps extends CommentCardBaseProps {
  parentEl: CommentActionsChildrenProps['parentEl'];
  onAddReaction: CommentActionsChildrenProps['onAddReaction'];
  onRemoveReaction: CommentActionsChildrenProps['onRemoveReaction'];
  onUpdate: CommentActionsChildrenProps['onUpdate'];
  onUpdateSchedule?: CommentActionsChildrenProps['onUpdateSchedule'];
  onDelete: CommentActionsChildrenProps['onDelete'];
}

export type CommentCardProps = CommentCardParentProps | CommentCardChildrenProps;

const DELETE_MESSAGE = '[Comment deleted]';

const highlightSearchTerm = (html: string, searchTerm: string): string => {
  if (!searchTerm || !html) return html;

  const doc = new DOMParser().parseFromString(html, 'text/html');
  const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const regex = new RegExp(escapedTerm, 'gi');

  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (!regex.test(text)) {
        regex.lastIndex = 0;
        return;
      }
      regex.lastIndex = 0;

      const frag = document.createDocumentFragment();
      let lastIndex = 0;

      let match = regex.exec(text);
      while (match !== null) {
        const [fullMatch] = match;
        if (match.index > lastIndex) {
          frag.appendChild(document.createTextNode(text.slice(lastIndex, match.index)));
        }
        const mark = document.createElement('mark');
        mark.className = 'search-highlight';
        mark.textContent = fullMatch;
        frag.appendChild(mark);
        lastIndex = match.index + fullMatch.length;
        match = regex.exec(text);
      }

      if (lastIndex < text.length) {
        frag.appendChild(document.createTextNode(text.slice(lastIndex)));
      }

      node.parentNode?.replaceChild(frag, node);
    } else if (node.nodeType === Node.ELEMENT_NODE) {
      Array.from(node.childNodes).forEach(walk);
    }
  };

  walk(doc.body);
  return doc.body.innerHTML;
};

const CommentCard: FC<CommentCardProps> = (props) => {
  const {
    comment,
    user,
    projectUuid,
    typeUuid,
    targetCommentId,
    scrollingContainer,
    mentionTypes,
    mentionableUrl,
    prefetchedMentionables,
    type,
    isProjectPersonal,
    canComment,
    searchTerm = '',
    onImgDelete,
    onReplyTo,
    onQuoteTo,
    onScrollToComment,
    onImageModalChanged,
    commentReplies,
  } = props;
  const [editing, setEditing] = useState(false);
  const [showEditorToolbar, setShowEditorToolbar] = useState(false);
  const [isEditorMultiline, setIsEditorMultiline] = useState(false);
  const singleLineHeightRef = useRef(38);
  const [quotedRemoved, setQuotedRemoved] = useState(false);
  const [editingScheduleModal, setEditingScheduleModal] = useState(false);
  const [images, setImages] = useState<UploadFileApi[]>([]);
  const [loadingImages, setLoadingImages] = useState(0);
  const [uploadModalVisible, setUploadModalVisible] = useState(false);
  const [fileViewerOpen, setFileViewerOpen] = useState(false);
  const [fileViewerIndex, setFileViewerIndex] = useState(0);
  const [dragState, setDragState] = useState<'allowed' | 'blocked' | null>(null);

  const commentRef = useRef<RichTextEditorHandle>(null);
  const wrapEditing = editing && !comment.deleted_at;

  const [hasEmojiResults, setHasEmojiResults] = useState(false);
  const [popoverCoords, setPopoverCoords] = useState({ top: 0, left: 0 });
  const [emojiSearch, setEmojiSearch] = useState<{ active: boolean; query: string; index: number }>({
    active: false,
    query: '',
    index: 0
  });

  const parsedText = comment.text_delta ? deltaToHtml(comment.text_delta) : comment.text;

  const bgTargetComment = targetCommentId === comment.uuid ? '#e2e2e2' : undefined;
  const bgPrivate = comment.type === 'private' ? 'yellow' : undefined;
  let commentOpacity = 0.6;

  const handleEmojiSelect = (emoji: { id: string; native: string }) => {
    const quill = commentRef.current?.getQuill()?.getEditor();
    if (quill) {
      let { index } = quill.getSelection(true);

      if (emojiSearch.active) {
        const lengthToRemove = emojiSearch.query.length + 1;
        quill.deleteText(emojiSearch.index, lengthToRemove);
        index = emojiSearch.index;
      }

      quill.insertEmbed(index, 'emoji-inline', {
        id: emoji.id,
        native: emoji.native
      });

      quill.setSelection(index + 1, 0);
      setEmojiSearch(prev => ({ ...prev, active: false }));
    }
  };

  const emojiPickerContent = (
    <div ref={(ref) => {
        if (ref) ref.onclick = (e) => e.stopPropagation();
    }}>
      <EmojiPicker
        onSelect={(emoji) => {
          handleEmojiSelect(emoji);
        }}
      />
    </div>
  );

  if (!comment.send_at || editing) {
    commentOpacity = 1;
  }

  const handleAddImages = (uploads: UploadFileApi[]) => {
    setImages((prev) => [...prev, ...uploads]);
    setUploadModalVisible(false);
  };

  let actions: JSX.Element | undefined;

  if (!comment.deleted_at && canComment) {
    actions = props.parentEl ? (
      <CommentActions
        key="actions"
        comment={comment}
        user={user}
        images={images}
        loadingImages={loadingImages}
        editing={wrapEditing}
        quotedRemoved={quotedRemoved}
        editorText={commentRef}
        parentEl={props.parentEl}
        type={type}
        typeUuid={typeUuid}
        onEdit={setEditing}
        onReplyTo={onReplyTo}
        onQuoteTo={onQuoteTo}
        onAddReaction={props.onAddReaction}
        onRemoveReaction={props.onRemoveReaction}
        onUpdate={props.onUpdate}
        onDelete={props.onDelete}
      />
    ) : (
      <CommentActions
        key="actions"
        comment={comment}
        user={user}
        images={images}
        loadingImages={loadingImages}
        editing={wrapEditing}
        quotedRemoved={quotedRemoved}
        editorText={commentRef}
        type={type}
        typeUuid={typeUuid}
        onEdit={setEditing}
        onReplyTo={onReplyTo}
        onQuoteTo={onQuoteTo}
        onAddReaction={props.onAddReaction}
        onRemoveReaction={props.onRemoveReaction}
        onUpdate={props.onUpdate}
        onDelete={props.onDelete}
      />
    );
  }

  useEffect(() => {
    if (wrapEditing) return;
    const editorVal = mentionCommentParser(parsedText);
    const highlighted = searchTerm ? highlightSearchTerm(editorVal, searchTerm) : editorVal;
    if (commentRef.current?.getValue() !== highlighted) {
      commentRef.current?.updateValue(highlighted);
    }
  }, [parsedText, searchTerm, wrapEditing]);

  useEffect(() => {
    if (!editing) {
      setImages([]);
      setLoadingImages(0);
      setQuotedRemoved(false);
      setEmojiSearch(prev => ({ ...prev, active: false }));
      setHasEmojiResults(false);
      setShowEditorToolbar(false);
    }
  }, [editing]);

  useEffect(() => {
    if (!editing) return;
    const timer = setTimeout(() => {
      const editorEl = commentRef.current?.getQuill()?.getEditor()?.root;
      if (!editorEl) return;
      const cs = window.getComputedStyle(editorEl);
      const lineHeight = parseFloat(cs.lineHeight) || 23;
      const paddingTop = parseFloat(cs.paddingTop) || 5;
      const paddingBottom = parseFloat(cs.paddingBottom) || 5;
      singleLineHeightRef.current = lineHeight + paddingTop + paddingBottom + 4;
      const isEmpty = !editorEl.textContent?.trim();
      setIsEditorMultiline((prev) => {
        if (isEmpty) return false;
        if (prev) return true;
        return editorEl.scrollHeight > singleLineHeightRef.current + 5;
      });
    }, 0);
    return () => clearTimeout(timer);
  }, [editing, showEditorToolbar]);

  if (comment.send_at && comment.deleted_at) {
    return null;
  }

  if (comment.send_at && user.uuid !== comment.creator.uuid) {
    return null;
  }

  const editorValue = mentionCommentParser(parsedText);

  return (
    <>
      <div
        className={`${styles.commentWrapper} comment-${comment.uuid}`}
        style={{
          borderRadius: 5,
          background: bgTargetComment ?? bgPrivate ?? undefined,
          position: 'relative',
          marginRight: 15,
        }}
      >
        <Comment
          author={
            <div style={{ opacity: commentOpacity }}>
            <Space style={{ minHeight: 22 }}>
                <Text>{comment.creator?.name}</Text>
                {comment.send_at ? (
                  <div className="flex items-center" style={{ gap: '.5rem' }}>
                    <Text style={{ color: '#fa8c16', opacity: 0.8 }}>
                      {parseDateToUSFormat(comment.send_at, true)} [Scheduled]
                    </Text>
                    <EditOutlined
                      onClick={() => setEditingScheduleModal(true)}
                      className="pointer"
                      style={{ fontSize: '16px' }}
                    />
                  </div>
                ) : (
                  <Text className="disabled">{parseDateToUSFormat(comment.created_at, true)}</Text>
                )}
                {!props.parentEl && type === 'projects' && (
                  <Tag color={comment.type === 'private' ? 'gold' : 'blue'}>{convertSnakeCaseToTitleCase(comment.type)}</Tag>
                )}
                {comment.created_at !== comment.updated_at && !comment.deleted_at && !comment.send_at && (
                  <Text className="disabled">[Edited]</Text>
                )}
              </Space>

              {comment.creator?.job_title ? (
                <div style={{ lineHeight: 1, marginBottom: 4 }}>{comment.creator?.job_title}</div>
              ) : <div style={{ marginBottom: 16 }} />}
            </div>
          }
          avatar={<div style={{ opacity: commentOpacity }}><UserAvatarPopover user={comment.creator} /></div>}
          content={
            <div>
              <div style={{ opacity: commentOpacity }}>
              {editorValue.toLowerCase() !== DELETE_MESSAGE.toLowerCase() && comment.quoted && !wrapEditing && (
                <div
                  className={`${styles.quotedComment} ${comment.deleted_at && styles.deletedQuoteComment}`}
                  style={{ cursor: 'pointer' }}
                  title="Jump to original message"
                  onClick={() => onScrollToComment?.(comment.quoted.uuid)}
                >
                  <div className={styles.quotedLine} />
                  <div className={styles.quotedContent}>
                    <strong><FaQuoteLeft size={10} style={{ marginRight: 5, opacity: 0.5 }} />{comment.quoted.creator?.name}</strong>
                    <RichTextEditor
                      key={comment.quoted.uuid}
                      className="comment-styles resume"
                      initialValue={mentionCommentParser(
                        comment.quoted.text_delta ? deltaToHtml(comment.quoted.text_delta) : comment.quoted.text,
                      )}
                      readonly
                      noContainer
                    />
                  </div>
                </div>
              )}

              {editing && (
                <FileUploadModal
                  visible={uploadModalVisible}
                  onOk={handleAddImages}
                  closeUploadModal={() => setUploadModalVisible(false)}
                />
              )}

              <div className={wrapEditing ? `${styles.chatInputWrapper}${dragState ? ` drag-overlay-parent drag-overlay-parent--${dragState}` : ''}` : undefined}>
                {comment.quoted && wrapEditing && !quotedRemoved && (
                  <div className={styles.replyTo} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                    <div className={styles.line} />
                    <div className={styles.quotedContent} style={{ flex: 1 }}>
                      <strong><FaQuoteLeft size={10} style={{ marginRight: 5, opacity: 0.5 }} />{comment.quoted.creator?.name}</strong>
                      <RichTextEditor
                        key={`edit-quoted-${comment.quoted.uuid}`}
                        className="comment-styles resume"
                        initialValue={mentionCommentParser(
                          comment.quoted.text_delta ? deltaToHtml(comment.quoted.text_delta) : comment.quoted.text,
                        )}
                        readonly
                        noContainer
                      />
                    </div>
                    <CloseOutlined
                      style={{ fontSize: 12, opacity: 0.5, cursor: 'pointer', flexShrink: 0, paddingTop: 2 }}
                      onClick={() => setQuotedRemoved(true)}
                    />
                  </div>
                )}

                <div className={wrapEditing ? `${styles.chatInputEditorRow}${isEditorMultiline ? ` ${styles.chatInputEditorRowMultiline}` : ''}` : undefined}>
                  <RichTextEditor
                    ref={commentRef}
                    className={`mention-compact${wrapEditing ? ' chat-input' : ' comment-styles'}${
                      comment.deleted_at ? ' deleted-message' : ''
                    }`}
                    placeholder="Add a comment"
                    initialValue={editorValue}
                    projectId={projectUuid}
                    mentionableUrl={mentionableUrl}
                    prefetchedMentionables={prefetchedMentionables}
                    scrollingContainer={scrollingContainer}
                    includeToolbar={showEditorToolbar}
                    includeMention={!isProjectPersonal}
                    mentionTypes={mentionTypes}
                    readonly={!wrapEditing}
                    noContainer={!wrapEditing}
                    style={{ backgroundColor: 'transparent' }}
                    progressBar="top"
                    imageAsData64
                    hideDropOverlay
                    onImageModalChanged={onImageModalChanged}
                    onDragStateChange={wrapEditing ? setDragState : undefined}
                    onLoadingImage={(value) => {
                      if (editing) setLoadingImages(value);
                    }}
                    onImageUploader={async (upload) => {
                      setImages((prev) => [...prev, upload]);
                    }}
                    onChange={(content, delta, source, editor) => {
                        if (editing) {
                          const editorEl = commentRef.current?.getQuill()?.getEditor()?.root;
                          const isEmpty = !content || content === '<p><br></p>';
                          setIsEditorMultiline((prev) => {
                            if (isEmpty) return false;
                            if (prev) return true;
                            return (editorEl?.scrollHeight ?? 0) > singleLineHeightRef.current + 5;
                          });
                        }
                        if (source === 'user' && editing) {
                          const ops = delta.ops || [];
                          const lastOp = ops[ops.length - 1];
                          const quill = editor;

                          if (lastOp?.insert === ':') {
                            const range = quill.getSelection();
                            if (range) {
                              const colonIndex = range.index - 1;
                              const charBefore = colonIndex > 0 ? quill.getText(colonIndex - 1, 1) : '';
                              const ALLOWED_BEFORE_COLON = /^(\s|\uFEFF|\uFFFC|)$/;

                              if (ALLOWED_BEFORE_COLON.test(charBefore)) {
                                const quillInstance = commentRef.current?.getQuill()?.getEditor();
                                const containerRect = quillInstance?.container?.getBoundingClientRect();
                                const bounds = quill.getBounds(range.index);
                                if (containerRect) {
                                  setPopoverCoords({
                                    top: containerRect.top + bounds.top,
                                    left: containerRect.left + bounds.left,
                                  });
                                }
                                setEmojiSearch({ active: true, query: '', index: colonIndex });
                                setHasEmojiResults(true);
                              }
                            }
                          } else if (emojiSearch.active) {
                            const currentRange = quill.getSelection();
                            if (currentRange) {
                              if (currentRange.index <= emojiSearch.index) {
                                setEmojiSearch(prev => ({ ...prev, active: false }));
                                setHasEmojiResults(false);
                              } else {
                                const textSinceColon = quill.getText(emojiSearch.index + 1, currentRange.index - (emojiSearch.index + 1));
                                if (textSinceColon.includes(' ') || textSinceColon.includes('\n')) {
                                  setEmojiSearch(prev => ({ ...prev, active: false }));
                                  setHasEmojiResults(false);
                                } else {
                                  setEmojiSearch(prev => ({ ...prev, query: textSinceColon }));
                                }
                              }
                            }
                          }
                        }
                      }}
                  />
                  {editing && (
                    <div className={styles.chatInputActions}>
                      <span className={styles.chatInputToolbarToggle}>
                        <Tooltip title="Formatting options">
                          <Button
                            type="text"
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            icon={<MdOutlineTextFormat style={{ fontSize: '18px' }} />}
                            onClick={() => setShowEditorToolbar(!showEditorToolbar)}
                          />
                        </Tooltip>
                      </span>
                      <Tooltip title="Add emoji">
                        <Popover
                          content={emojiPickerContent}
                          trigger="click"
                          placement="bottomLeft"
                          getPopupContainer={() => document.body}
                          overlayClassName={`no-padding-popover ${styles.emojiPortal}`}
                        >
                          <Button
                            type="text"
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            icon={<FaRegSmile style={{ fontSize: '18px' }} />}
                          />
                        </Popover>
                      </Tooltip>
                      <Tooltip title="Upload file">
                        <Button
                          type="text"
                          style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                          icon={<UploadOutlined style={{ fontSize: '18px' }} />}
                          onClick={() => setUploadModalVisible(true)}
                        />
                      </Tooltip>
                    </div>
                  )}
                </div>

              </div>

              <CommentUploads
                comment={comment}
                user={user}
                editing={editing}
                images={images}
                setImages={setImages}
                onImgDelete={onImgDelete}
                setFileViewerIndex={setFileViewerIndex}
                setFileViewerOpen={setFileViewerOpen}
              />
              </div>
              {actions}
            </div>
          }
          key={comment.uuid}
          style={{ padding: '0 10px' }}
        />
      </div>

      {commentReplies && !props.parentEl && <div className={styles.repliesContainer}>{commentReplies}</div>}

      {editing && emojiSearch.active && (
        <PickerPositioner top={popoverCoords.top} left={popoverCoords.left} visible={hasEmojiResults}>
          <EmojiPicker
            onSelect={handleEmojiSelect}
            searchQuery={emojiSearch.query}
            inline
            onResultsCountChange={(count) => setHasEmojiResults(count > 0)}
            onClose={() => {
              setEmojiSearch(prev => ({ ...prev, active: false }));
              setHasEmojiResults(false);
            }}
          />
        </PickerPositioner>
      )}

      {editingScheduleModal && (
        <CommentSchedule
          visible={editingScheduleModal}
          onClose={() => setEditingScheduleModal(false)}
          onSubmit={(sendAt) => {
            props.onUpdateSchedule?.(comment.uuid, sendAt, () => setEditingScheduleModal(false));
          }}
          initialSendAt={comment.send_at}
        />
      )}

      <FileViewerModal
        files={comment.uploads}
        initialIndex={fileViewerIndex}
        open={fileViewerOpen}
        onClose={() => setFileViewerOpen(false)}
      />
    </>
  );
};

export default CommentCard;
